import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authorizedFetch } from "../auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type { SpiffeCluster, UpdateSpiffeClusterRequest } from "./types";

const API_BASE = import.meta.env.VITE_KEYCLOAK_API_URL ?? "/keycloak-api";

export function useSpiffeClusters() {
  return useQuery({
    queryKey: ["spiffe-clusters"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_BASE}/spiffe/clusters`);
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<SpiffeCluster[]>;
    },
    staleTime: 1000 * 60,
  });
}

export function useApproveSpiffeCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (cluster: string) => {
      const res = await authorizedFetch(
        `${API_BASE}/spiffe/clusters/${encodeURIComponent(cluster)}/approve`,
        { method: "POST" },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<SpiffeCluster>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spiffe-clusters"] });
    },
  });
}

export function useRejectSpiffeCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (cluster: string) => {
      const res = await authorizedFetch(
        `${API_BASE}/spiffe/clusters/${encodeURIComponent(cluster)}/reject`,
        { method: "POST" },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spiffe-clusters"] });
    },
  });
}

export function useUpdateSpiffeCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cluster,
      body,
    }: {
      cluster: string;
      body: UpdateSpiffeClusterRequest;
    }) => {
      const res = await authorizedFetch(
        `${API_BASE}/spiffe/clusters/${encodeURIComponent(cluster)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<SpiffeCluster>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spiffe-clusters"] });
    },
  });
}

export function useDeleteSpiffeCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (cluster: string) => {
      const res = await authorizedFetch(
        `${API_BASE}/spiffe/clusters/${encodeURIComponent(cluster)}`,
        { method: "DELETE" },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spiffe-clusters"] });
    },
  });
}
